
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabase, ensureSupabaseInitialized, isSupabaseConnected } from '@/lib/supabaseClient';
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useTermsManagement } from '@/contexts/hooks/useTermsManagement';
import { useProfileData } from '@/hooks/useProfileData';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { useAuthSession } from '@/contexts/hooks/useAuthSession';
import { useAuthActions } from '@/contexts/hooks/useAuthActions';
import { useAvatarUpload } from '@/hooks/useAvatarUpload';

function useAuthContextLogic() {
      const { 
        user, 
        session,
        loadingSession, 
        initialAuthCheckComplete: sessionAuthCheckComplete, 
        userRole, 
        isEmailConfirmed,
        refreshAuthData,
        lastAuthEvent,
      } = useAuthSession();
      
      const [loadingAuthAction, setLoadingAuthAction] = useState(false);
      const [authError, setAuthError] = useState(null); 
      const [supabaseConfigured, setSupabaseConfigured] = useState(false);
      const [forceRender, setForceRender] = useState(false);

      const navigate = useNavigate();
      const { toast } = useToast();
      const { t, isReady: isLanguageReady } = useLanguage();
      const { settings, isLoading: isLoadingSiteSettings } = useSiteSettings();
      const currentTermsVersion = useMemo(() => settings?.current_terms_version || '1.0', [settings]);

      const {
        profile,
        loadingProfile,
        errorProfile,
        updateUserProfile,
        refetchProfile,
      } = useProfileData(user?.id);

      const {
        uploadAvatar,
        isUploading: isUploadingAvatar
      } = useAvatarUpload(user?.id);

      const { 
        onboardingStatus, 
        loadingStatus: loadingOnboardingStatus, 
        errorStatus: errorOnboardingStatus, 
        updateOnboardingStatus: updateProfileOnboardingFlags,
        refetchOnboardingStatus
      } = useOnboardingStatus(user, profile);
      
      const {
        showTermsDialog,
        setShowTermsDialog,
        handleAcceptTerms,
        closeTermsDialog,
        checkTermsAgreement,
      } = useTermsManagement(user, profile, loadingProfile, isLoadingSiteSettings, currentTermsVersion, updateUserProfile, refetchProfile, onboardingStatus, updateProfileOnboardingFlags);

      const {
        loginUser,
        registerUser,
        logoutUser,
        requestPasswordReset,
        updateUserPassword,
        resendConfirmationEmail,
        requestAccountDeletion,
      } = useAuthActions(setLoadingAuthAction, refetchProfile, setShowTermsDialog, checkTermsAgreement, refreshAuthData);

      const clearAuthError = useCallback(() => {
        setAuthError(null);
      }, []);

      useEffect(() => {
        const checkSupabaseConfig = async () => {
          setLoadingAuthAction(true);
          try {
            await ensureSupabaseInitialized();
            if (isSupabaseConnected()) {
              setSupabaseConfigured(true);
            } else {
              throw new Error("Supabase client failed to initialize or connect.");
            }
          } catch (err) {
            console.error("[AuthContextLogic] Supabase initialization error:", err);
            setAuthError({ message: "Failed to initialize authentication service. Please try again later." });
            setSupabaseConfigured(false);
          } finally {
            setLoadingAuthAction(false); 
          }
        };
        checkSupabaseConfig();
        
        // Force render after 5 seconds to prevent infinite loading
        const timeoutId = setTimeout(() => {
          setForceRender(true);
        }, 5000);
        
        return () => clearTimeout(timeoutId);
      }, []);

      // Run onboarding/terms checks once per user session to avoid loops/remount storms
      const initializedForUserRef = useRef(null);
      useEffect(() => {
        if (!user || !sessionAuthCheckComplete || loadingProfile) return;
        // Ignore token refresh-only events
        if (lastAuthEvent === 'TOKEN_REFRESHED') return;
        if (initializedForUserRef.current === user.id) return;
        initializedForUserRef.current = user.id;
        // Trigger a single refresh of onboarding status and a single terms check
        refetchOnboardingStatus();
        checkTermsAgreement();
      }, [user?.id, sessionAuthCheckComplete, loadingProfile, refetchOnboardingStatus, checkTermsAgreement, lastAuthEvent]);


      // Do not block the entire app on site settings; use sane defaults and render
      const overallLoading = useMemo(() => {
        const isLoading = loadingSession || loadingAuthAction || loadingProfile || loadingOnboardingStatus || !sessionAuthCheckComplete || !supabaseConfigured;
        
        // If force render is true, don't block the app
        return forceRender ? false : isLoading;
      }, [loadingSession, loadingAuthAction, loadingProfile, loadingOnboardingStatus, sessionAuthCheckComplete, supabaseConfigured, isLoadingSiteSettings, forceRender]);

      const combinedError = useMemo(() => {
        return authError || errorProfile || errorOnboardingStatus;
      }, [authError, errorProfile, errorOnboardingStatus]);


      const contextValue = useMemo(() => ({
        user,
        session,
        profile,
        loading: overallLoading,
        isLoadingAuthAction: loadingAuthAction,
        error: combinedError,
        loginUser,
        registerUser,
        logoutUser,
        requestPasswordReset,
        updateUserPassword,
        updateUserProfile,
        requestAccountDeletion,
        refetchProfile,
        uploadAvatar,
        isUploadingAvatar,
        initialAuthCheckComplete: sessionAuthCheckComplete,
        clearAuthError,
        currentTermsVersion,
        checkTermsAgreement,
        setShowTermsDialog,
        supabaseConfigured,
        onboardingStatus,
        updateOnboardingStatus: updateProfileOnboardingFlags,
        isEmailConfirmed,
        userRole,
        resendConfirmationEmail
      }), [
        user, session, profile, overallLoading, loadingAuthAction, combinedError, loginUser, registerUser, logoutUser, requestPasswordReset, updateUserPassword, updateUserProfile, requestAccountDeletion, refetchProfile, sessionAuthCheckComplete, clearAuthError,
        currentTermsVersion, checkTermsAgreement, setShowTermsDialog, supabaseConfigured, onboardingStatus, updateProfileOnboardingFlags, isEmailConfirmed, userRole, resendConfirmationEmail, uploadAvatar, isUploadingAvatar
      ]);

      return {
        contextValue,
        user,
        loading: overallLoading,
        error: combinedError,
        profile,
        showTermsDialog,
        handleAcceptTerms,
        closeTermsDialog,
        supabaseConfigured,
        initialAuthCheckComplete: sessionAuthCheckComplete
      };
    }
    
    export { useAuthContextLogic };
  
